<template>
  <div
    v-if="patternProduct.product"
    class="flex w-full flex-col gap-2 py-4 md:w-full md:flex-col md:py-2 lg:grid lg:grid-cols-[3fr,5fr]"
  >
    <div class="flex items-center gap-4">
      <a
        class="relative isolate shrink-0"
        :href="`${patternProduct.product.handle}?${variantURLParam}`"
        target="_blank"
      >
        <Image
          :src="imgSrc"
          img-classes="w-24 lg:w-24 "
          :lazy-load="true"
          :width="96"
          :height="96"
          loader="fade"
          class="overflow-hidden rounded-md border bg-gray-50"
          :alt="patternProduct.product.title"
        />
        <span
          v-if="patternProduct.product.discountPercentage > 0"
          class="absolute -right-1 -top-2 z-10 flex aspect-square rotate-6 items-center justify-center rounded-full bg-yellow p-1 text-xs font-bold shadow"
        >
          {{ patternProduct.product.discountPercentage }}%
        </span>
      </a>
      <div class="flex h-full grow flex-col justify-center">
        <span
          v-if="patternProduct.product?.brand"
          class="text-sm font-semibold text-gray-700"
          >{{ patternProduct.product.brand }}</span
        >
        <a
          v-if="patternProduct.product?.handle"
          class="font-bold leading-tight"
          :href="`${patternProduct.product.handle}?${variantURLParam}`"
          target="_blank"
        >
          {{ patternProduct.product.title }}
        </a>

        <div class="text-sm text-gray-500">
          <span>{{ currentVariant?.title }}</span>
          <span v-if="currentVariant?.weight" class="px-2">&bull;</span>
          <span v-if="patternProduct.product.type === 'yarn'"
            >{{ currentVariant?.weight }}
          </span>
        </div>

        <div class="mt-1 flex gap-2 text-base">
          <span
            class="font-bold"
            :class="
              compareAtPriceWithCurrencySymbol ? 'text-secondary' : 'text-black'
            "
          >
            {{ priceWithCurrencySymbol }}
          </span>
          <span
            v-if="patternProduct.product.compareAtPrice"
            class="text-gray-400 line-through"
          >
            {{ compareAtPriceWithCurrencySymbol }}
          </span>
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-2 md:flex-row">
      <div
        v-if="patternProduct.product.variants.nodes.length > 1"
        class="flex grow items-center"
      >
        <VariantSelectorModal
          :options="patternProduct.product.variants.nodes"
          :active="currentVariant"
          :heading="t('patterns.products.select-variant')"
          @option-click="handleVariantChange"
        />
      </div>
      <div v-else class="flex grow items-center justify-end">
        <StockIndicator
          v-if="currentVariant"
          class="flex text-sm"
          :in-stock="currentVariant.quantityAvailable"
        />
      </div>

      <div class="flex items-center justify-end gap-2">
        <div class="flex grow basis-1/2 justify-end">
          <QuantityBasicSelector
            :is-independent="true"
            :show-title="false"
            :pre-selected-qty="patternProduct.quantity"
            @independent-qty-change="handleQuantityChange"
          />
        </div>
        <div class="">
          <ButtonAddToCart
            ref="addToCartButton"
            :disabled="!productInStock"
            @click="onAddToCartClick"
            ><IconCart class="hidden text-lg xs:block"
          /></ButtonAddToCart>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue';

import IconCart from '@/shared/components/UI/Icons/IconCart.vue';
import Image from '@/shared/components/UI/Image.vue';
import StockIndicator from '@/shared/components/UI/StockIndicator.vue';
import {
  PatternProduct,
  Variant
} from '@/shared/composables/patternProducts/patternProducts.types';
import { useTranslations } from '@/shared/composables/useTranslations';
import { useCartStore } from '@/shared/pinia/modules/cart.ts';
import { getErrorMessage } from '@/shared/utils/error';
import { shopifyGidToId } from '@/shared/utils/shopify/id-formatter';
import { formatPriceStorefront } from '@/shared/utils/shopify/money-formatter';
import { trackHobbiiEvent } from '@/shared/utils/tracking';

import ButtonAddToCart from '../CartActions/ButtonAddToCart.vue';
import QuantityBasicSelector from '../QuantitySelector/QuantityBasicSelector.vue';
import VariantSelectorModal from './VariantSelectorModal.vue';

const emit = defineEmits(['change']);

const props = defineProps<{
  patternProduct: PatternProduct;
  index: number;
}>();

const { t } = useTranslations();
const cartStore = useCartStore();
const addToCartButtonRef = useTemplateRef('addToCartButton');

const isAdding = ref(false);
const currentQuantity = ref<number | undefined>(props.patternProduct.quantity);
const currentVariant = ref<Variant | null>(
  props.patternProduct.product?.variant || null
);

const variantURLParam = computed(() => {
  return currentVariant.value
    ? `variant=${shopifyGidToId(currentVariant.value.id)}`
    : '';
});

const handleVariantChange = (variant: Variant, oldVariant: Variant | null) => {
  currentVariant.value = variant;

  if (!oldVariant) {
    emit('change', {
      productId: props.patternProduct.product_id,
      variantId: currentVariant.value?.id,
      quantity: currentQuantity.value,
      index: props.index
    });
  }
};
const handleQuantityChange = (newQuantity: number, oldQuantity: number) => {
  currentQuantity.value = newQuantity;
  if (!oldQuantity) {
    emit('change', {
      productId: props.patternProduct.product_id,
      variantId: currentVariant.value?.id,
      quantity: currentQuantity.value,
      index: props.index
    });
  }
};

const productInStock = computed(
  () => (currentVariant.value?.quantityAvailable || 0) > 0
);

const getTrackingItem = () =>
  productInStock.value
    ? {
        item_id: props.patternProduct.product_id,
        item_name: props.patternProduct.product?.title,
        item_category: props.patternProduct.product?.type,
        item_variant: currentVariant.value?.title,
        price: parseFloat(currentVariant.value?.price.amount),
        quantity: currentQuantity.value
      }
    : null;

const trackAddToCart = () => {
  const trackingItem = getTrackingItem();
  if (!trackingItem) {
    return;
  }

  trackHobbiiEvent('pattern_products_add_to_cart', {
    currency: window.Shopify.currency.active,
    items: [trackingItem],
    value:
      parseFloat(currentVariant.value?.price.amount) *
      (currentQuantity.value || 0),
    category: trackingItem.item_category || 'item',
    label: trackingItem.item_name
  });
};

const onAddToCartClick = async () => {
  try {
    isAdding.value = true;

    const cartItem = getCartItem();

    if (!cartItem) return;

    await cartStore.addIndependentProductsToCart([cartItem]);
    trackAddToCart();
  } catch (err) {
    const errorMessage = getErrorMessage(err);

    if (errorMessage) {
      addToCartButtonRef?.value?.displayError(errorMessage);
    }
  } finally {
    isAdding.value = false;
  }
};

const priceWithCurrencySymbol = computed(() => {
  const price = props.patternProduct?.product?.price;
  return price ? formatPriceStorefront(price) : '';
});

const compareAtPriceWithCurrencySymbol = computed(() => {
  const compareAtPrice = props.patternProduct?.product?.compareAtPrice;
  if (!parseFloat(compareAtPrice?.amount)) return '';

  return compareAtPrice ? formatPriceStorefront(compareAtPrice) : '';
});

const imgSrc = computed(() => {
  const url =
    currentVariant.value?.image.url || props.patternProduct.product?.image.url;
  if (url) {
    const imgSrc = new URL(url);
    imgSrc.searchParams.set('width', '200');
    imgSrc.searchParams.set('height', '200');
    imgSrc.searchParams.set('q', '80');
    return imgSrc.toString();
  }
  return '';
});

const getCartItem = () => {
  return currentVariant.value && currentQuantity.value && productInStock.value
    ? {
        id: shopifyGidToId(currentVariant.value.id || ''),
        quantity:
          currentVariant.value.quantityAvailable >= currentQuantity.value
            ? currentQuantity.value
            : currentVariant.value.quantityAvailable
      }
    : null;
};

// TODO: refactor this so we don't use expose at all, and do everything uni-directionally, with emits or the cart store
defineExpose({
  getCartItem,
  getTrackingItem
});
</script>
